import { Observable } from 'rxjs';
import { InjectionToken } from '../../injection';

import { EmptyResponse } from '../data/empty-response.do';
import { RegisterViewModel } from '../data/register-view-model.do';
import { ResetPasswordViewModel } from '../data/reset-password-view-model.do';
import { SignUpViewModel } from '../data/sign-up-view-model.do';
import { VerificationId } from '../data/verification-id.do';
import { VerificationStatus } from '../data/verification-status.do';

export interface IAccountService {

  /**
   * Signs a user up.
   * @param signUp The sign-up request data
   * @return VerificationId to be used when checking the email verification
   */
  signUp(signUp: SignUpViewModel): Observable<VerificationId>;

  /**
   * Checks if a sign-up email has been verified
   * @param verificationId The identifier of the verification to be checked
   * @return VerificationStatus The status of the verification
   */
  checkEmailVerificationStatus(verificationId: VerificationId): Observable<VerificationStatus>;

  /**
   * Sends the verification email again, keeping the verification id
   * @param verificationId The verification id of reference
   */
  resendVerificationEmail(verificationId: VerificationId): Observable<EmptyResponse>;

  /**
   * Register user.
   * @param registration The registration request
   */
  register(registration: RegisterViewModel): Observable<RegisterViewModel>;

  /**
   * Change the account notification email.
   */
  changeNotificationEmail(email: string): Observable<EmptyResponse>;

  /**
   * Reset account password.
   * @param payload The reset password payload
   */
  resetPassword(payload: ResetPasswordViewModel): Observable<RegisterViewModel>;

  /**
   * Change account password
   * @param input Contains the old and new password
   */
  changePassword(input: { oldPassword: string; password: string; confirmPassword: string }): Observable<EmptyResponse>;

  /**
   * Confirms adding new email to account profile
   * @param payload Contains the user ID and the security code
   */
  confirmEmail(payload: {userId: string; code: string}): Observable<EmptyResponse>;

  /**
   * Sends an email message with the verification link to the specified email address
   * @param email The email address to be verified
   */
  requestEmailVerification(email: string): Observable<EmptyResponse>;

  /**
   * Sends a verification SMS with a token to the specified phone
   * @param phoneNumber The phone number to be verified
   * @return VerificationId to be used when checking the token
   */
  requestPhoneNumberVerificationToken(phoneNumber: string): Observable<VerificationId>;

  /**
   * Sends an email message with the verification link to the specified email address
   * @param verificationId The id of the verification process
   * @param token The token received via SMS
   */
  checkPhoneNumberVerificationToken(verificationId: VerificationId, token: string): Observable<EmptyResponse>;

}
export const IAccountService = new InjectionToken('IAccountService');
